import { useEmailLoginSubmit } from '@/components/modal/sign-in-modal/hooks/useEmailLoginSubmit';
import { useSignInModalState } from '@/components/modal/sign-in-modal/hooks/useSignInModalState';
import { signInClicked } from '@/lib/analytics/scribe/auth';
import { useDesktopAuthStore } from '@/models/desktopAuthSlice';
import { beginDesktopKindeAttempt } from '@/tauri/auth0/login';
import { useDesktopLoginPending } from '@/tauri/auth0/use-desktop-login-pending';
import { isTauri, isUsingReactKinde } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { useTranslations } from '@/utils/translations';
import { Button } from '@heidi/design-system/button';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { isEmpty } from 'lodash-es';
import { type ReactNode, useState } from 'react';
import Loader from '../../../ui/loader';
import { iframeSafeAuthRoute } from '../utils';

interface DBLoginButtonProps {
  email: string;
  isEmailInvalid: boolean;
  /** Callback when button is clicked (before auth redirect) */
  onClick?: () => void;
  /**
   * Desktop only: the click ended without a desktop attempt to wait on
   * (Identity Broker error/Kinde fallback), so the surface can release any
   * shared in-progress state it set in `onClick` (MUL-3346).
   */
  onAttemptAbandoned?: () => void;
  /** External disabled state (e.g., when another button is loading) */
  disabled?: boolean;
  /** Full redirect URI including locale prefix */
  postLoginRedirectUri: string;
  emailConnectionId: string;
  /** Show loading state immediately (e.g., for mobile auto-submit) */
  forceLoading?: boolean;
  label?: ReactNode;
}

/**
 * DB/Email login button that conditionally renders Kinde or Auth0 based on feature flag.
 */
export function DBLoginButton({
  email,
  isEmailInvalid,
  onClick,
  onAttemptAbandoned,
  disabled,
  postLoginRedirectUri,
  emailConnectionId,
  forceLoading,
  label,
}: DBLoginButtonProps) {
  const { t, locale } = useTranslations();
  const { register: kindeRegister } = useKindeAuth();
  const shouldUseDesktopAuth = isTauri();
  const [isLoading, setIsLoading] = useState(false);

  const { submitViaDetermineLogin, isDetermineLoginEnabled, isDetermineLoginFetching } =
    useEmailLoginSubmit({
      email,
      postLoginRedirectUri,
      emailConnectionId,
    });

  const { isFromMobileWebview } = useSignInModalState({ postLoginRedirectUri });

  const handleClick = () => {
    signInClicked('email');
    setIsLoading(true);
    onClick?.();
  };

  // Desktop auth resolves in-place (no redirect): hold the loading state from
  // the click until the attempt settles (session, auth error, launch failure,
  // or the 5s abandonment watchdog), so a second click can never start a
  // second PKCE attempt while the browser leg runs (MUL-3346). The shared
  // pending flag also honors an attempt started from a sibling button.
  const isDesktopLoginPending = useDesktopLoginPending(() => setIsLoading(false));
  const isDisabled =
    disabled || isLoading || isDetermineLoginFetching || isDesktopLoginPending;

  // Kinde branches: exact pre-MUL-3346 semantics plus pending exclusion. On a
  // FORCE_KINDE desktop build this IS the live email button, so `isLoading`
  // must keep disabling on every platform (the pre-PR double-click guard);
  // only the external `disabled` prop was desktop-exempt pre-PR.
  const isKindeDisabled =
    (shouldUseDesktopAuth ? false : disabled) ||
    isLoading ||
    isDetermineLoginFetching ||
    isDesktopLoginPending;
  const buttonLabel = label ?? t('auth.continueWithEmail');

  // Render active button when email is empty — triggers validation via
  // callback. Deliberately ignores the web `disabled` prop (clicking must
  // surface the "enter your email" error), but still locks while a desktop
  // attempt is in flight: every sign-in control disables during the browser
  // leg (MUL-3346), and the pending flag is never set on web.
  if (isEmpty(email)) {
    return (
      <Button
        disableAutomaticSvgSizing
        className="w-full"
        size="lg"
        type="button"
        disabled={isDesktopLoginPending}
        onClick={onClick}
      >
        {buttonLabel}
      </Button>
    );
  }

  // Auth0 path - uses Identity Broker to determine auth provider
  // Use feature flag for production
  // OR
  // If the user is on a mobile webview we use bypass the feature flag and use determine login since we can't open the feature flag during the test
  if (isDetermineLoginEnabled || isFromMobileWebview) {
    return (
      <Button
        disableAutomaticSvgSizing
        size="lg"
        disabled={isEmpty(email) || isEmailInvalid || isDisabled}
        className="w-full"
        data-testid="sign-in-email-button"
        type="button"
        onClick={() => {
          handleClick();

          // Call Identity Broker API to determine auth provider
          void submitViaDetermineLogin()
            .catch((error) => {
              // The broker axios error carries the typed email in its config —
              // log a PII-safe projection only (Datadog transport, MUL-3346).
              logger.error('[DBLoginButton] determine-login submit failed', {
                message: error instanceof Error ? error.message : String(error),
              });
            })
            .finally(() => {
              // Routing that ends in a desktop Auth0 launch marks the shared
              // pending attempt before this settles; any other outcome
              // (Kinde fallback, broker error, rejection) leaves no attempt
              // to wait on, so release the spinner instead of stranding the
              // button — and tell the surface so it can release its own
              // shared in-progress state too.
              if (
                shouldUseDesktopAuth &&
                useDesktopAuthStore.getState().loginPendingSince === null
              ) {
                setIsLoading(false);
                onAttemptAbandoned?.();
              }
            });
        }}
      >
        {isLoading || forceLoading ? (
          <Loader className="size-6 text-text-primary-invert" />
        ) : (
          buttonLabel
        )}
      </Button>
    );
  }

  // Kinde path - React Kinde (Button)
  if (isUsingReactKinde()) {
    return (
      <Button
        disableAutomaticSvgSizing
        size="lg"
        disabled={isEmpty(email) || isEmailInvalid || isKindeDisabled}
        className="w-full"
        onClick={() => {
          handleClick();
          // Desktop: this hands off to the system browser and never comes
          // back with a settle signal — mark the attempt so the abandonment
          // watchdog releases the spinner and the surface's shared disable.
          beginDesktopKindeAttempt();
          void kindeRegister({
            authUrlParams: {
              login_hint: email,
              post_login_redirect_url: postLoginRedirectUri,
              connection_id: emailConnectionId,
              lang: locale,
            },
            app_state: {
              redirectTo: postLoginRedirectUri,
            },
          });
        }}
      >
        {isLoading ? <Loader className="size-6 text-text-primary-invert" /> : buttonLabel}
      </Button>
    );
  }

  // Kinde path - Link (API routes)
  return (
    <Button
      disableAutomaticSvgSizing
      size="lg"
      disabled={isKindeDisabled || isEmpty(email) || isEmailInvalid}
      className="w-full"
      type="button"
      data-testid="sign-in-email-button"
      onClick={() => {
        handleClick();
        const url = new URL('/api/auth/register', window.location.origin);
        url.searchParams.set('login_hint', email);
        url.searchParams.set('post_login_redirect_url', postLoginRedirectUri);
        url.searchParams.set('connection_id', emailConnectionId);
        iframeSafeAuthRoute(url.toString());
      }}
    >
      {isLoading ? <Loader className="size-6 text-text-primary-invert" /> : buttonLabel}
    </Button>
  );
}
